<script>
  import { onMount } from 'svelte';
  import { customDaSources, showToast, officialDaSources, loadOfficialDaSources } from './stores.js';
  import { downloadFile } from './exportUtils.js';

  let activeTab = 'official';
  let customDa = $customDaSources;
  let newListName = '';
  let newEntry = { type: 'employee', effective_date: '', to: 0 };
  let lastModified = {};
  let searchTerm = '';
  let undoStack = [];
  let redoStack = [];
  let applyToBoth = false;
  let showAddForm = {};
  let lastAddedEntry = {}; // Track the last added entry per list for highlighting

  onMount(async () => {
    loadOfficialDaSources();
  });

  $: customDa = $customDaSources;

  function handleExport(type, listName) {
    let data;
    if (type === 'official') {
      data = $officialDaSources[listName];
      downloadFile(JSON.stringify({ [listName]: data }, null, 2), `${listName}-official-da.json`, 'application/json');
    } else {
      data = customDa[listName];
      downloadFile(JSON.stringify({ [listName]: data }, null, 2), `${listName}-custom-da.json`, 'application/json');
    }
  }

  function handleImport(type, event) {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (typeof e.target.result !== 'string') throw new Error('File is not text');
        const data = JSON.parse(e.target.result);
        if (type === 'official') {
          officialDaSources.update(existing => ({ ...existing, ...data }));
          showToast('Official DA lists imported (not saved permanently)', 'info');
        } else {
          customDaSources.update(existing => ({ ...existing, ...data }));
          showToast('Custom DA lists imported!', 'success');
        }
      } catch (err) {
        showToast('Import failed: ' + err.message, 'error');
      }
    };
    reader.readAsText(file);
  }

  function cloneToCustom(listName) {
    let baseName = `Copy of ${listName}`;
    let copyName = baseName;
    let counter = 1;
    while (customDa[copyName]) {
      counter += 1;
      copyName = `${baseName} (${counter})`;
    }
    customDaSources.update(existing => ({ ...existing, [copyName]: [...$officialDaSources[listName]] }));
    updateLastModified(copyName);
    showToast(`Official list cloned as '${copyName}'`, 'success');
  }

  function addCustomList() {
    if (!newListName.trim()) {
      showToast('List name required', 'error');
      return;
    }
    if (customDa[newListName]) {
      showToast('Custom list with this name already exists!', 'error');
      return;
    }
    customDaSources.update(existing => ({ ...existing, [newListName]: [] }));
    updateLastModified(newListName);
    newListName = '';
    showToast('Custom list created!', 'success');
  }

  function deleteCustomList(listName) {
    if (!confirm(`Delete custom list '${listName}'?`)) return;
    customDaSources.update(existing => {
      const copy = { ...existing };
      delete copy[listName];
      return copy;
    });
    delete lastModified[listName];
    showToast('Custom list deleted.', 'info');
  }

  function addEntryToCustom(listName) {
    if (!newEntry.effective_date || newEntry.to === undefined || newEntry.to === null) {
      showToast('All fields required', 'error');
      return;
    }
    const entryEmployee = { ...newEntry };
    const entriesToAdd = applyToBoth ? [ { ...entryEmployee, type: 'employee' }, { ...entryEmployee, type: 'pensioner' } ] : [ entryEmployee ];

    customDaSources.update(existing => {
      const copy = { ...existing };
      copy[listName] = [...copy[listName], ...entriesToAdd];
      // Sort ascending by effective date
      copy[listName].sort((a,b) => new Date(a.effective_date).getTime() - new Date(b.effective_date).getTime());
      // Remember last added (first of entriesToAdd)
      lastAddedEntry[listName] = entriesToAdd[0];
      return copy;
    });
    updateLastModified(listName);
    newEntry = { type: 'employee', effective_date: '', to: 0 };
    showToast('Entry added!', 'success');
  }

  function prefillNewEntryDate(listName) {
    const entries = customDa[listName] || [];
    let lastDate = null;
    if (entries.length > 0) {
      const filtered = entries.filter(e => applyToBoth ? true : e.type === newEntry.type);
      if (filtered.length > 0) {
        lastDate = filtered.reduce((latest, entry) => {
          return (!latest || entry.effective_date > latest) ? entry.effective_date : latest;
        }, null);
      }
    }
    if (lastDate) {
      const d = new Date(lastDate);
      d.setMonth(d.getMonth() + 6);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      newEntry.effective_date = `${yyyy}-${mm}-${dd}`;
    } else {
      newEntry.effective_date = '';
    }
  }

  function editEntryInCustom(listName, entryToUpdate, field, value) {
    customDaSources.update(existing => {
      const copy = { ...existing };
      const index = copy[listName].indexOf(entryToUpdate);
      if (index !== -1) {
        copy[listName][index] = { ...copy[listName][index], [field]: value };
        // Resort list after edit
        copy[listName].sort((a,b) => new Date(a.effective_date).getTime() - new Date(b.effective_date).getTime());
      }
      return copy;
    });
    updateLastModified(listName);
    showToast('Entry updated!', 'success');
  }

  function deleteEntryFromCustom(listName, entryToDelete) {
    if (!confirm('Delete this entry?')) return;
    customDaSources.update(existing => {
      const copy = { ...existing };
      copy[listName] = copy[listName].filter(e => e !== entryToDelete);
      return copy;
    });
    updateLastModified(listName);
    showToast('Entry deleted.', 'info');
  }

  function updateLastModified(name) {
    lastModified[name] = new Date().toLocaleString();
  }

  function pushUndo(state) {
    undoStack.push(JSON.stringify(state));
    if (undoStack.length > 50) undoStack.shift();
  }

  function undo(listName) {
    if (undoStack.length === 0) return;
    redoStack.push(JSON.stringify(customDa[listName]));
    const prev = JSON.parse(undoStack.pop());
    customDaSources.update(existing => ({ ...existing, [listName]: prev }));
    updateLastModified(listName);
  }

  function redo(listName) {
    if (redoStack.length === 0) return;
    pushUndo(customDa[listName]);
    const next = JSON.parse(redoStack.pop());
    customDaSources.update(existing => ({ ...existing, [listName]: next }));
    updateLastModified(listName);
  }

  function filterEntries(entries) {
    let result = entries;
    if (searchTerm.trim()) {
      result = result.filter(e =>
        e.effective_date.includes(searchTerm) ||
        String(e.to).includes(searchTerm)
      );
    }
    // Always sort ascending (oldest first)
    result = result.slice().sort((a,b) => new Date(a.effective_date).getTime() - new Date(b.effective_date).getTime());
    return result;
  }
</script>

<div class="da-manager-view glass-card">
  <h2>DA Manager</h2>
  <div class="tab-bar">
    <button class:active={activeTab === 'official'} on:click={() => activeTab = 'official'}>Official Lists</button>
    <button class:active={activeTab === 'custom'} on:click={() => activeTab = 'custom'}>Custom Lists</button>
  </div>

  {#if activeTab === 'official'}
    <div class="da-list-section">
      <h3>Official DA Lists</h3>
      <div class="da-list-table">
        {#each Object.keys($officialDaSources) as listName}
          <div class="da-list-row">
            <div class="da-list-title">{listName}</div>
            <button on:click={() => handleExport('official', listName)}>Export</button>
            <button on:click={() => cloneToCustom(listName)}>Clone to Custom</button>
            <label class="import-btn">
              Import
              <input type="file" accept="application/json" on:change={(e) => handleImport('official', e)} style="display:none;" />
            </label>
            <details>
              <summary>Show Entries</summary>
              <table class="da-table">
                <thead>
                  <tr><th>Type</th><th>Effective Date</th><th>DA %</th></tr>
                </thead>
                <tbody>
                  {#each $officialDaSources[listName] as entry}
                    <tr>
                      <td>{entry.type}</td>
                      <td>{entry.effective_date}</td>
                      <td>{entry.to}</td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </details>
          </div>
        {/each}
      </div>
    </div>
  {:else}
    <div class="da-list-section">
      <h3>Custom DA Lists</h3>
      <div class="add-list-bar">
        <input type="text" placeholder="New list name" bind:value={newListName} />
        <button on:click={addCustomList}>Add List</button>
      </div>
      <div class="da-list-controls">
        <input type="text" placeholder="Search by date or DA%" bind:value={searchTerm} aria-label="Search entries" />
      </div>
      <div class="da-list-table">
        {#each Object.keys(customDa) as listName}
          <div class="da-list-row">
            <div class="da-list-title">{listName} <span class="last-modified">(Last modified: {lastModified[listName]})</span></div>
            <button on:click={() => deleteCustomList(listName)}>Delete</button>
            <label class="import-btn">
              Import
              <input type="file" accept="application/json" on:change={(e) => handleImport('custom', e)} style="display:none;" />
            </label>
            <details open>
              <summary>Show/Edit Entries</summary>
              <table class="da-table">
                <thead>
                  <tr><th>Type</th><th>Effective Date</th><th>DA %</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {#each filterEntries(customDa[listName]) as entry}
                    <tr class:duplicate={customDa[listName].filter(e => e.effective_date === entry.effective_date).length > 1}
                        class:new-entry={lastAddedEntry[listName] === entry}>
                      <td>
                        <select bind:value={entry.type} on:change={e => editEntryInCustom(listName, entry, 'type', e.currentTarget.value)} title="Type: Employee or Pensioner">
                          <option value="employee">Employee</option>
                          <option value="pensioner">Pensioner</option>
                        </select>
                      </td>
                      <td><input type="date" bind:value={entry.effective_date} on:change={e => editEntryInCustom(listName, entry, 'effective_date', e.currentTarget.value)} title="Effective Date" /></td>
                      <td><input type="number" bind:value={entry.to} min="0" step="0.01" on:change={e => editEntryInCustom(listName, entry, 'to', e.currentTarget.value)} title="DA %" /></td>
                      <td><button on:click={() => deleteEntryFromCustom(listName, entry)} title="Delete entry">Delete</button></td>
                    </tr>
                  {/each}
                  
                  {#if !showAddForm[listName]}
                    <tr>
                      <td colspan="4" class="add-entry-cell">
                        <button class="add-entry-btn" on:click={() => { showAddForm[listName] = true; prefillNewEntryDate(listName); }} type="button">
                          <span class="plus-icon">+</span> Add New Entry
                        </button>
                      </td>
                    </tr>
                  {:else}
                    <tr class="add-entry-form-row">
                      <td>
                        <select bind:value={newEntry.type} disabled={applyToBoth}>
                          <option value="employee">Employee</option>
                          <option value="pensioner">Pensioner</option>
                        </select>
                        <label class="apply-both-label">
                          <input type="checkbox" bind:checked={applyToBoth} />
                          Apply to both
                        </label>
                      </td>
                      <td><input type="date" bind:value={newEntry.effective_date} /></td>
                      <td><input type="number" bind:value={newEntry.to} min="0" step="0.01" placeholder="DA %" /></td>
                      <td class="action-cell">
                        <button on:click={() => addEntryToCustom(listName)} class="btn-success">Add</button>
                        <button on:click={() => showAddForm[listName] = false} class="btn-secondary">Cancel</button>
                      </td>
                    </tr>
                  {/if}
                </tbody>
              </table>
            </details>
          </div>
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
.da-manager-view {
  max-width: 900px;
  margin: 40px auto;
  padding: 32px;
}
.tab-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}
.tab-bar button {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 6px 6px 0 0;
  padding: 10px 24px;
  font-size: 1.1rem;
  cursor: pointer;
  color: var(--text-color);
  transition: background 0.2s;
}
.tab-bar button.active {
  background: var(--primary-color);
  color: #fff;
  border-bottom: 2px solid var(--primary-color-dark, #16a34a);
}
.da-list-section {
  margin-top: 20px;
}
.da-list-table {
  margin-top: 20px;
}
.da-list-row {
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 8px;
  margin-bottom: 18px;
  padding: 18px;
}
.da-list-title {
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 8px;
}
.add-list-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
}
.add-list-bar input {
  flex: 1;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid var(--glass-border);
}
.da-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}
.da-table th, .da-table td {
  border: 1px solid var(--glass-border);
  padding: 6px 10px;
  text-align: left;
  background: var(--glass-bg);
  color: var(--text-color);
}
.import-btn {
  display: inline-block;
  margin-left: 12px;
  cursor: pointer;
  color: var(--primary-color);
  font-weight: 500;
}
.last-modified {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}
.da-list-controls {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
}
.duplicate {
  background: #fff3cd !important;
}
.new-entry {
  background: rgba(34, 197, 94, 0.2) !important; /* subtle green highlight */
}
.add-entry-cell {
  text-align: center;
  padding: 12px 0;
}
.add-entry-btn {
  background: transparent;
  border: 1px dashed var(--primary-color);
  color: var(--primary-color);
  padding: 10px 20px;
  cursor: pointer;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.add-entry-btn:hover {
  background: rgba(34, 197, 94, 0.1);
  border-style: solid;
}
.plus-icon {
  font-size: 1.5rem;
  line-height: 1;
}
.add-entry-form-row td {
  background-color: rgba(34, 197, 94, 0.05);
}
.add-entry-form-row input, .add-entry-form-row select {
  width: 100%;
  padding: 8px;
  border: 1px solid var(--glass-border);
  border-radius: 6px;
  background-color: var(--glass-bg);
  color: var(--text-color);
}
.apply-both-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  margin-top: 6px;
  cursor: pointer;
}
.action-cell {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn-success {
  background: var(--primary-color);
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
}
.btn-secondary {
  background: var(--glass-bg);
  color: var(--text-color);
  border: 1px solid var(--glass-border);
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
}
</style> 